<template>
  <modal 
    name="churned-contracts-modal" 
    :adaptive="true"
    :scrollable="true"
    height="auto"
    :max-width="900"
    width="90%"
    class="churned-contracts-modal"
  >
    <div class="p-6">
      <!-- Header -->
      <div class="flex items-center justify-between mb-6 border-b border-secondary-200 pb-4">
        <div class="flex items-center gap-3">
          <div class="p-2 bg-orange-100 rounded-lg">
            <svg class="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"></path>
            </svg>
          </div>
          <div>
            <h2 class="text-xl font-bold text-secondary-800">{{ $t('sales_dashboard.churned_contracts') }}</h2>
            <p class="text-sm text-secondary-500">{{ churnedContracts.length }} {{ $t('sales_dashboard.contracts_label') }}</p>
          </div>
        </div>
        <button 
          @click="$modal.hide('churned-contracts-modal')" 
          class="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
        >
          <svg class="w-5 h-5 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
          </svg>
        </button>
      </div>

      <!-- Summary Stats -->
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div class="bg-orange-50 rounded-lg p-4">
          <p class="text-xs text-orange-600 font-medium uppercase tracking-wide">{{ $t('sales_dashboard.total_contracts') }}</p>
          <p class="text-2xl font-bold text-orange-700 mt-1">{{ churnedContracts.length }}</p>
        </div>
        <div class="bg-red-50 rounded-lg p-4">
          <p class="text-xs text-red-600 font-medium uppercase tracking-wide">{{ $t('sales_dashboard.lost_mrr') }}</p>
          <p class="text-2xl font-bold text-red-700 mt-1">{{ formatCurrency(totalLostMrr) }}</p>
        </div>
        <div class="bg-secondary-50 rounded-lg p-4">
          <p class="text-xs text-secondary-600 font-medium uppercase tracking-wide">{{ $t('sales_dashboard.total_products') }}</p>
          <p class="text-2xl font-bold text-secondary-700 mt-1">{{ totalProducts }}</p>
        </div>
        <div class="bg-blue-50 rounded-lg p-4">
          <p class="text-xs text-blue-600 font-medium uppercase tracking-wide">{{ $t('sales_dashboard.avg_contract_length') }}</p>
          <p class="text-2xl font-bold text-blue-700 mt-1">{{ avgContractLength }} {{ $t('sales_dashboard.month_short') }}</p>
        </div>
      </div>

      <!-- Contracts Table -->
      <div class="border border-secondary-200 rounded-lg overflow-hidden">
        <div class="overflow-x-auto max-h-96">
          <table class="min-w-full divide-y divide-secondary-200">
            <thead class="bg-secondary-50 sticky top-0">
              <tr>
                <th class="px-4 py-3 text-left text-xs font-semibold text-secondary-600 uppercase tracking-wider">
                  {{ $t('sales_dashboard.customer') }}
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-secondary-600 uppercase tracking-wider">
                  {{ $t('sales_dashboard.salesperson') }}
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-secondary-600 uppercase tracking-wider">
                  {{ $t('sales_dashboard.status') }}
                </th>
                <th class="px-4 py-3 text-right text-xs font-semibold text-secondary-600 uppercase tracking-wider">
                  MRR
                </th>
                <th class="px-4 py-3 text-center text-xs font-semibold text-secondary-600 uppercase tracking-wider">
                  {{ $t('sales_dashboard.products') }}
                </th>
                <th class="px-4 py-3 text-left text-xs font-semibold text-secondary-600 uppercase tracking-wider">
                  {{ $t('sales_dashboard.ended_date') }}
                </th>
              </tr>
            </thead>
            <tbody class="bg-white divide-y divide-secondary-100">
              <tr 
                v-for="contract in churnedContracts" 
                :key="contract._id || contract.id"
                class="hover:bg-secondary-50 transition-colors"
              >
                <td class="px-4 py-3">
                  <div class="text-sm font-medium text-secondary-800">{{ contract.customerName || contract.customer_name || '-' }}</div>
                  <div class="text-xs text-secondary-500">{{ contract.agreementNumber || contract.agreement_number || '-' }}</div>
                </td>
                <td class="px-4 py-3 text-sm text-secondary-600">
                  {{ contract.seller || contract.salesperson_name || '-' }}
                </td>
                <td class="px-4 py-3">
                  <span 
                    class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                    :class="getStatusClass(contract.status)"
                  >
                    {{ getStatusLabel(contract.status) }}
                  </span>
                </td>
                <td class="px-4 py-3 text-sm text-right font-medium text-red-600">
                  {{ formatCurrency(contract.monthlyValue || contract.mrr || contract.rental_month_cost || 0) }}
                </td>
                <td class="px-4 py-3 text-center">
                  <button 
                    v-if="contract.products && contract.products.length > 0"
                    @click="toggleProducts(contract._id || contract.id)"
                    class="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                  >
                    <span class="font-medium">{{ contract.productCount || getProductCount(contract) }} {{ $t('sales_dashboard.pieces_unit') }}</span>
                    <svg 
                      class="w-3 h-3 transition-transform" 
                      :class="{ 'rotate-180': expandedContracts.includes(contract._id || contract.id) }"
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </button>
                  <span v-else class="text-xs text-secondary-400">{{ contract.productCount || 0 }}</span>
                </td>
                <td class="px-4 py-3 text-sm text-secondary-600">
                  {{ formatDate(contract.endDate || contract.ended_at || contract.contract_end) }}
                </td>
              </tr>
              
              <!-- Expanded Products Row -->
              <template v-for="contract in churnedContracts">
                <tr 
                  v-if="expandedContracts.includes(contract._id || contract.id) && contract.products"
                  :key="(contract._id || contract.id) + '-products'"
                  class="bg-secondary-50"
                >
                  <td colspan="6" class="px-4 py-3">
                    <div class="pl-4 border-l-2 border-blue-300">
                      <p class="text-xs font-semibold text-secondary-600 mb-2">{{ $t('sales_dashboard.products') }}:</p>
                      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                        <div 
                          v-for="(product, pIdx) in contract.products" 
                          :key="pIdx"
                          class="bg-white rounded p-2 text-xs border border-secondary-200"
                        >
                          <p class="font-medium text-secondary-800 truncate">{{ product.name || product.type || $t('sales_dashboard.product') }}</p>
                          <p class="text-secondary-500">
                            {{ product.qty || product.quantity || 1 }} {{ $t('sales_dashboard.pieces_unit') }}
                            <span v-if="product.rental_price || product.price"> • {{ formatCurrency(product.rental_price || product.price) }}{{ $t('sales_dashboard.per_month_short') }}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Footer -->
      <div class="mt-6 flex justify-end">
        <button 
          @click="$modal.hide('churned-contracts-modal')"
          class="px-4 py-2 bg-secondary-100 text-secondary-700 rounded-lg hover:bg-secondary-200 transition-colors text-sm font-medium"
        >
          {{ $t('common.close') }}
        </button>
      </div>
    </div>
  </modal>
</template>

<script>
export default {
  name: 'ChurnedContractsModal',
  props: {
    contracts: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      expandedContracts: []
    };
  },
  computed: {
    churnedContracts() {
      return this.contracts.filter(c => 
        ['returned', 'dismissed'].includes(c.status) &&
        ['rental', 'financed'].includes(c.category)
      );
    },
    totalLostMrr() {
      return this.churnedContracts.reduce((sum, c) => {
        return sum + parseFloat(c.monthlyValue || c.mrr || c.rental_month_cost || 0);
      }, 0);
    },
    totalProducts() {
      return this.churnedContracts.reduce((sum, c) => {
        if (c.productCount) return sum + c.productCount;
        if (!c.products) return sum;
        return sum + c.products.reduce((pSum, p) => pSum + (p.qty || p.quantity || 1), 0);
      }, 0);
    },
    avgContractLength() {
      if (this.churnedContracts.length === 0) return 0;
      const totalMonths = this.churnedContracts.reduce((sum, c) => {
        const period = parseInt(c.contractPeriod || c.contract_period || c.contract_length_months || 0);
        return sum + period;
      }, 0);
      return Math.round(totalMonths / this.churnedContracts.length);
    }
  },
  methods: {
    formatCurrency(value) {
      if (!value && value !== 0) return '-';
      return new Intl.NumberFormat('sv-SE', {
        style: 'currency',
        currency: 'SEK',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value);
    },
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString('sv-SE');
    },
    getProductCount(contract) {
      if (!contract.products) return 0;
      return contract.products.reduce((sum, p) => sum + (p.qty || p.quantity || 1), 0);
    },
    getStatusClass(status) {
      const classes = {
        returned: 'bg-orange-100 text-orange-800',
        dismissed: 'bg-red-100 text-red-800'
      };
      return classes[status] || 'bg-secondary-100 text-secondary-800';
    },
    getStatusLabel(status) {
      const labels = {
        returned: this.$t('sales_dashboard.status_returned'),
        dismissed: this.$t('sales_dashboard.status_dismissed')
      };
      return labels[status] || status;
    },
    toggleProducts(contractId) {
      const idx = this.expandedContracts.indexOf(contractId);
      if (idx > -1) {
        this.expandedContracts.splice(idx, 1);
      } else {
        this.expandedContracts.push(contractId);
      }
    }
  }
};
</script>

<style scoped>
.churned-contracts-modal :deep(.vm--modal) {
  border-radius: 12px;
}
</style>
